import "./styles/main.css";
import { applyLang, detectLang, toggleLang } from "./i18n";
import type { Lang } from "./i18n/copy";
import { createBoorhubScene, type SceneApi } from "./scene/boorhub-scene";
import {
  applyTheme,
  detectTheme,
  toggleTheme,
  watchSystemTheme,
  type Theme,
} from "./theme";

function hidePreloader(): void {
  const el = document.getElementById("preloader");
  if (!el) return;
  el.classList.add("is-done");
  window.setTimeout(() => el.remove(), 450);
}

function initSmoothAnchors(): void {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
  if (isTouch) return;
  document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => {
    a.addEventListener("click", (event) => {
      const href = a.getAttribute("href");
      if (!href || href === "#") return;
      const target = document.querySelector(href);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
    });
  });
}

function bindAccordion(): void {
  const root = document.getElementById("faq_accordion");
  if (!root) return;
  root.querySelectorAll<HTMLElement>(".accordion-item").forEach((item) => {
    const btn = item.querySelector<HTMLButtonElement>(".accordion-button");
    const panel = item.querySelector<HTMLElement>(".accordion-panel");
    if (!btn || !panel) return;
    btn.onclick = () => {
      const open = item.classList.contains("is-open");
      root.querySelectorAll<HTMLElement>(".accordion-item.is-open").forEach((other) => {
        if (other === item) return;
        other.classList.remove("is-open");
        other
          .querySelector<HTMLButtonElement>(".accordion-button")
          ?.setAttribute("aria-expanded", "false");
        const otherPanel = other.querySelector<HTMLElement>(".accordion-panel");
        if (otherPanel) otherPanel.hidden = true;
      });
      if (open) {
        item.classList.remove("is-open");
        btn.setAttribute("aria-expanded", "false");
        panel.hidden = true;
      } else {
        item.classList.add("is-open");
        btn.setAttribute("aria-expanded", "true");
        panel.hidden = false;
      }
    };
  });
}

function initScrollChrome(): void {
  const nav = document.querySelector<HTMLElement>(".topnav");
  const links = Array.from(
    document.querySelectorAll<HTMLAnchorElement>(".topnav-links a[href^='#']"),
  );
  const sections = ["hero", "services", "policy"]
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => Boolean(el));

  const apply = () => {
    const y = window.scrollY;
    nav?.classList.toggle("is-scrolled", y > 18);

    let current = "hero";
    const mark = y + window.innerHeight * 0.28;
    for (const section of sections) {
      if (section.offsetTop <= mark) current = section.id;
    }
    links.forEach((link) => {
      const href = link.getAttribute("href")?.slice(1);
      link.classList.toggle("is-current", href === current);
    });
  };

  window.addEventListener("scroll", apply, { passive: true });
  window.addEventListener("resize", apply, { passive: true });
  apply();
}

let revealObserver: IntersectionObserver | null = null;

function initReveal(): void {
  revealObserver?.disconnect();
  revealObserver = null;
  const nodes = document.querySelectorAll<HTMLElement>(
    ".section-head, .service-card, .accordion-item, .site-footer",
  );
  if (!nodes.length) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    nodes.forEach((el) => el.classList.add("is-in"));
    return;
  }
  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add("is-in");
        revealObserver?.unobserve(entry.target);
      }
    },
    { threshold: 0.16, rootMargin: "0px 0px -8% 0px" },
  );
  nodes.forEach((el) => {
    el.classList.add("reveal");
    const box = el.getBoundingClientRect();
    if (box.top < window.innerHeight * 0.92 && box.bottom > 40) {
      el.classList.add("is-in");
      return;
    }
    revealObserver?.observe(el);
  });
}

function scrollProgress(): number {
  const doc = document.documentElement;
  const max = Math.max(1, doc.scrollHeight - window.innerHeight);
  return window.scrollY / max;
}

function bindServiceCards(api: SceneApi): void {
  const cards = Array.from(
    document.querySelectorAll<HTMLElement>("[data-service]"),
  );
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  cards.forEach((card) => {
    card.onmouseenter = () => {
      const i = Number(card.dataset.service);
      if (!Number.isFinite(i)) return;
      api.setActiveService(i);
      cards.forEach((c) => {
        c.classList.toggle("is-active", Number(c.dataset.service) === i);
      });
    };
  });
}

function initScene(theme: Theme): SceneApi | null {
  const mount = document.getElementById("stage");
  if (!mount) return null;

  const api = createBoorhubScene(mount, {
    onReady: hidePreloader,
    theme,
  });
  const fill = document.getElementById("progress-fill");

  let active = -1;
  let scrollRaf = 0;

  const applyScroll = () => {
    scrollRaf = 0;
    const p = scrollProgress();
    api.setProgress(p);
    if (fill) fill.style.width = `${Math.round(p * 100)}%`;

    const cards = Array.from(
      document.querySelectorAll<HTMLElement>("[data-service]"),
    );
    let best = -1;
    let bestDist = Infinity;
    const mid = window.innerHeight * 0.45;
    for (const card of cards) {
      const r = card.getBoundingClientRect();
      const c = (r.top + r.bottom) / 2;
      const d = Math.abs(c - mid);
      if (r.bottom > 0 && r.top < window.innerHeight && d < bestDist) {
        bestDist = d;
        best = Number(card.dataset.service);
      }
    }
    if (best !== active && best >= 0) {
      active = best;
      api.setActiveService(best);
      cards.forEach((c) => {
        c.classList.toggle("is-active", Number(c.dataset.service) === best);
      });
    }
  };

  const onScroll = () => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(applyScroll);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener(
    "resize",
    () => {
      api.resize();
      onScroll();
    },
    { passive: true },
  );

  bindServiceCards(api);
  applyScroll();
  window.setTimeout(hidePreloader, 2000);
  return api;
}

function boot(): void {
  let lang: Lang = detectLang();
  let theme: Theme = detectTheme();

  applyLang(lang);
  void applyTheme(theme);
  bindAccordion();
  initScrollChrome();
  initReveal();

  initSmoothAnchors();
  const api = initScene(theme);

  document
    .querySelector<HTMLButtonElement>("#lang-toggle")
    ?.addEventListener("click", () => {
      lang = toggleLang(lang);
    });

  document
    .querySelector<HTMLButtonElement>("#theme-toggle")
    ?.addEventListener("click", () => {
      void toggleTheme(theme).then((next) => {
        theme = next;
        api?.setTheme(next);
      });
    });

  watchSystemTheme((next) => {
    theme = next;
    void applyTheme(next).then(() => api?.setTheme(next));
  });

  document.addEventListener("boorhub:lang", () => {
    bindAccordion();
    initReveal();
    if (api) bindServiceCards(api);
  });

  document.documentElement.classList.add("is-ready");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}

window.addEventListener("load", hidePreloader, { once: true });
